import { registerSubdomain, getSubdomainBySubdomain } from './subdomainService';
import { logSubdomainRegistration, logSubdomainDeletion, logApiCall, LOG_LEVELS } from './subdomainLogger';
import { createPage } from './firestoreService';

/**
 * 自動承認方式でサブドメインを登録します
 * @param {string} subdomain - サブドメイン
 * @param {string} accountId - アカウントID
 * @param {string} userEmail - ユーザーメール
 * @returns {Promise<Object>} 登録結果
 */
export const registerSubdomainAuto = async (subdomain, accountId, userEmail) => {
    try {
        console.log('🚀 自動サブドメイン登録開始:', subdomain);

        // 1. サブドメインの重複チェック
        const existingSubdomain = await getSubdomainBySubdomain(subdomain);
        if (existingSubdomain) {
            throw new Error('このサブドメインは既に使用されています');
        }

        // 2. Cloudflare DNSレコードを追加
        console.log('📡 Cloudflare DNSレコードを追加中...');
        const dnsResult = await addCloudflareDNSRecord(subdomain);
        logApiCall('Cloudflare', 'add', subdomain, dnsResult);

        if (!dnsResult.success) {
            throw new Error(`DNS設定に失敗しました: ${dnsResult.error}`);
        }

        // 3. Vercelドメインを追加
        console.log('⚡ Vercelドメインを追加中...');
        const vercelResult = await addVercelDomain(subdomain);
        logApiCall('Vercel', 'add', subdomain, vercelResult);

        if (!vercelResult.success) {
            // DNSは追加されたがVercelで失敗した場合、DNSを削除
            console.warn('⚠️ Vercelドメイン追加に失敗、DNSレコードを削除中...');
            const rollbackResult = await removeCloudflareDNSRecord(subdomain);
            logApiCall('Cloudflare', 'rollback', subdomain, rollbackResult);
            throw new Error(`Vercelドメイン設定に失敗しました: ${vercelResult.error}`);
        }

        // 4. データベースにサブドメイン情報を保存
        console.log('💾 データベースにサブドメイン情報を保存中...');
        const subdomainData = await registerSubdomain(subdomain, accountId, userEmail);

        // 5. 初期indexページを作成
        console.log('📄 初期indexページを作成中...');
        try {
            const indexPageData = {
                title: 'ようこそ',
                slug: 'index',
                parentId: '', // 親ページなし（ルートページ）
                html: `<!DOCTYPE html>
<html lang="ja">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>ようこそ - ${subdomain}.suisui.life</title>
    <meta name="description" content="ようこそ、${subdomain}.suisui.lifeへ">
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            margin: 0;
            padding: 40px 20px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .container {
            text-align: center;
            max-width: 600px;
        }
        h1 {
            font-size: 3rem;
            margin-bottom: 1rem;
            text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
        }
        p {
            font-size: 1.2rem;
            margin-bottom: 2rem;
            opacity: 0.9;
        }
        .cta {
            display: inline-block;
            background: rgba(255,255,255,0.2);
            padding: 15px 30px;
            border-radius: 50px;
            text-decoration: none;
            color: white;
            font-weight: bold;
            transition: all 0.3s ease;
            border: 2px solid rgba(255,255,255,0.3);
        }
        .cta:hover {
            background: rgba(255,255,255,0.3);
            transform: translateY(-2px);
        }
    </style>
</head>
<body>
    <div class="container">
        <h1>🎉 ようこそ！</h1>
        <p>あなたのウェブサイト「${subdomain}.suisui.life」が正常に作成されました。</p>
        <p>このページを編集して、あなただけのウェブサイトを作り上げましょう。</p>
        <a href="/admin" class="cta">管理画面へ</a>
    </div>
</body>
</html>`,
                css: '',
                status: 'draft', // 初期は非公開
                snsImage: '',
                colorPalette: '',
                disableColorPalette: false,
                headerPartId: '',
                footerPartId: '',
                customHead: '',
                customBody: '',
                accountId: accountId,
                userId: accountId, // アカウントIDをユーザーIDとして使用
                createdAt: new Date(),
                updatedAt: new Date()
            };

            await createPage(indexPageData);
            console.log('✅ 初期indexページを作成しました');
        } catch (pageError) {
            console.warn('⚠️ 初期indexページの作成に失敗:', pageError);
            // ページ作成に失敗してもサブドメイン登録は成功とする
        }

        // 6. 成功ログを記録
        const result = {
            success: true,
            subdomain: subdomainData,
            dnsRecord: dnsResult.record,
            vercelDomain: vercelResult.domain,
            message: `サブドメイン「${subdomain}.suisui.life」が正常に登録されました！`
        };

        logSubdomainRegistration(subdomain, accountId, result);
        return result;

    } catch (error) {
        console.error('❌ 自動サブドメイン登録エラー:', error);
        const errorResult = {
            success: false,
            error: error.message
        };
        logSubdomainRegistration(subdomain, accountId, errorResult);
        return errorResult;
    }
};

/**
 * Cloudflare DNSレコードを追加
 * @param {string} subdomain - サブドメイン
 * @returns {Promise<Object>} 追加結果
 */
const addCloudflareDNSRecord = async (subdomain) => {
    try {
        // 開発環境では模擬的な成功レスポンスを返す
        if (import.meta.env.DEV) {
            console.log('🔧 開発環境: Cloudflare DNS追加をシミュレーション');
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1秒の遅延
            return {
                success: true,
                record: {
                    id: 'mock-record-id',
                    name: `${subdomain}.suisui.life`,
                    content: 'cname.vercel-dns.com',
                    type: 'CNAME'
                },
                message: '開発環境でのシミュレーション成功'
            };
        }

        const response = await fetch('/api/cloudflare-dns', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                subdomain: subdomain,
                action: 'add'
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        return result;
    } catch (error) {
        console.error('Cloudflare DNS API エラー:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Vercelドメインを追加
 * @param {string} subdomain - サブドメイン
 * @returns {Promise<Object>} 追加結果
 */
const addVercelDomain = async (subdomain) => {
    try {
        // 開発環境では模擬的な成功レスポンスを返す
        if (import.meta.env.DEV) {
            console.log('🔧 開発環境: Vercelドメイン追加をシミュレーション');
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1秒の遅延
            return {
                success: true,
                domain: {
                    name: `${subdomain}.suisui.life`,
                    verified: true,
                    redirect: null
                },
                message: '開発環境でのシミュレーション成功'
            };
        }

        const response = await fetch('/api/vercel-domain', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                subdomain: subdomain,
                action: 'add'
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        return result;
    } catch (error) {
        console.error('Vercel Domain API エラー:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Cloudflare DNSレコードを削除
 * @param {string} subdomain - サブドメイン
 * @returns {Promise<Object>} 削除結果
 */
const removeCloudflareDNSRecord = async (subdomain) => {
    try {
        const response = await fetch('/api/cloudflare-dns', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                subdomain: subdomain,
                action: 'remove'
            })
        });

        const result = await response.json();
        return result;
    } catch (error) {
        console.error('Cloudflare DNS削除エラー:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * サブドメインの削除（管理者用）
 * @param {string} subdomain - サブドメイン
 * @returns {Promise<Object>} 削除結果
 */
export const deleteSubdomainAuto = async (subdomain) => {
    try {
        console.log('🗑️ サブドメイン削除開始:', subdomain);

        // 1. Cloudflare DNSレコードを削除
        const dnsResult = await removeCloudflareDNSRecord(subdomain);
        if (!dnsResult.success) {
            console.warn('⚠️ DNSレコード削除に失敗:', dnsResult.error);
        }

        // 2. Vercelドメインを削除
        const vercelResult = await removeVercelDomain(subdomain);
        if (!vercelResult.success) {
            console.warn('⚠️ Vercelドメイン削除に失敗:', vercelResult.error);
        }

        return {
            success: true,
            message: `サブドメイン「${subdomain}.suisui.life」が削除されました`,
            dnsResult,
            vercelResult
        };

    } catch (error) {
        console.error('❌ サブドメイン削除エラー:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Vercelドメインを削除
 * @param {string} subdomain - サブドメイン
 * @returns {Promise<Object>} 削除結果
 */
const removeVercelDomain = async (subdomain) => {
    try {
        const response = await fetch('/api/vercel-domain', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                subdomain: subdomain,
                action: 'remove'
            })
        });

        const result = await response.json();
        return result;
    } catch (error) {
        console.error('Vercel Domain削除エラー:', error);
        return {
            success: false,
            error: error.message
        };
    }
};
